/**
 * Retimes a running job's pending queue after the daily pace changes.
 *
 * Wait-step contacts keep their pause. Claimed (processing) contacts are left
 * alone. Everyone else fills today's leftover quota, then spreads across later
 * working days at the new limit — so raising the slider pulls later contacts
 * forward, and lowering it parks the overflow until tomorrow.
 */

import { pacedExecuteAts, stepWorkingDay } from './contactPoolLists';

const isPaceGated = (entry, steps) => {
  if (!entry || !entry.key) return false;
  if (entry.status && entry.status !== 'pending') return false;
  if (entry.processing) return false;
  return (steps[entry.currentStepIndex] || {}).type !== 'wait';
};

export const planPaceReschedule = ({
  entries = [],
  steps = [],
  now = Date.now(),
  newLimit,
  usedToday = 0,
  nextDayStartMs,
  skipWeekends = true,
  timezone = 'Europe/Berlin',
  alignNextDays = 'offset',
} = {}) => {
  const limit = Math.max(1, Number(newLimit) || 1);
  const used = Math.max(0, Number(usedToday) || 0);
  const remainingToday = Math.max(0, limit - used);
  const startMs = nextDayStartMs || stepWorkingDay(now, timezone, skipWeekends);

  const eligible = entries.filter((entry) => isPaceGated(entry, steps));
  eligible.sort((a, b) => (a.executeAt || 0) - (b.executeAt || 0) || String(a.key).localeCompare(String(b.key)));

  const due = [];
  const later = [];
  for (const entry of eligible) {
    ((entry.executeAt || 0) <= now ? due : later).push(entry);
  }

  const keepDueCount = Math.min(due.length, remainingToday);
  const overflowDue = due.slice(keepDueCount);
  const pullCount = Math.min(later.length, remainingToday - keepDueCount);
  const pullForward = later.slice(0, pullCount);
  const rest = overflowDue.concat(later.slice(pullCount));
  const futureAts = pacedExecuteAts(rest.length, {
    dailyLimit: limit,
    startMs,
    skipWeekends,
    timezone,
    alignNextDays,
  });

  const updates = {};
  for (const entry of pullForward) {
    if (entry.executeAt !== now) updates[entry.key] = now;
  }
  rest.forEach((entry, i) => {
    if (entry.executeAt !== futureAts[i]) updates[entry.key] = futureAts[i];
  });
  return updates;
};

export const usedTodayFromStats = (statsVal, { isPrepare, jobId } = {}) => {
  if (isPrepare) {
    if (!statsVal || typeof statsVal !== 'object') return 0;
    return Math.max(0, Number(statsVal.preparedByJob?.[jobId]) || 0);
  }
  if (typeof statsVal === 'number') return Math.max(0, statsVal);
  return Math.max(0, Number(statsVal?.total) || 0);
};
